<template>
  <hs-modal v-model="open" title="Edit Member" size="lg" @close="$emit('close')">
    <form id="egmm-form" class="egmm-form" @submit.prevent="handleSubmit">
      <div class="egmm-member-card">
        <div class="egmm-avatar">{{ getInitials(member.user_name) }}</div>
        <div>
          <p class="egmm-member-name">{{ member.user_name || "Unknown User" }}</p>
          <p class="egmm-member-email">{{ member.user_email || `User #${member.user_id}` }}</p>
        </div>
      </div>

      <group-permissions-selector
        v-model="form.permissions"
        :catalog="permissionCatalog"
        :loading="catalogLoading"
        :disabled="loading"
      />

      <hs-alert v-if="errorMessage" tone="danger" :message="errorMessage" />
    </form>

    <template #footer>
      <div class="egmm-footer">
        <hs-button variant="ghost" type="button" :disabled="loading" @click="$emit('close')">
          Cancel
        </hs-button>
        <hs-button
          variant="primary"
          icon="save"
          type="submit"
          form="egmm-form"
          :loading="loading || catalogLoading"
          :disabled="!hasChanges"
        >
          Save Changes
        </hs-button>
      </div>
    </template>
  </hs-modal>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { toast } from "vue3-toastify";
import http from "@/helper/http";
import api from "@/helper/api";
import GroupPermissionsSelector from "./GroupPermissionsSelector.vue";
import { HsModal, HsButton, HsAlert } from "@/components/ui";

export default {
  name: "EditGroupMemberModal",
  components: { HsModal, HsButton, HsAlert, GroupPermissionsSelector },
  props: {
    groupId: { type: Number, required: true },
    member: { type: Object, required: true },
  },
  emits: ["close", "updated"],
  setup(props, { emit }) {
    const open = ref(true);
    const loading = ref(false);
    const catalogLoading = ref(true);
    const errorMessage = ref("");
    const permissionCatalog = ref({});

    const form = ref({
      permissions: Array.isArray(props.member.permissions) ? [...props.member.permissions] : [],
    });

    const hasChanges = computed(() => {
      const current = [...(props.member.permissions || [])].map((p) => String(p).trim().toUpperCase()).filter(Boolean).sort();
      const next = [...(form.value.permissions || [])].map((p) => String(p).trim().toUpperCase()).filter(Boolean).sort();
      return JSON.stringify(current) !== JSON.stringify(next);
    });

    const loadPermissionCatalog = async () => {
      catalogLoading.value = true;
      try {
        const { data } = await http.get(api.group_permissions_catalog);
        permissionCatalog.value = data || {};
      } catch (err) {
        console.error("Failed to load group permission catalog", err);
        errorMessage.value = err?.response?.data?.error?.message || "Unable to load group permissions.";
      } finally {
        catalogLoading.value = false;
      }
    };

    const getInitials = (name) => {
      if (!name) return "?";
      return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
    };

    const handleSubmit = async () => {
      if (!hasChanges.value) { emit("close"); return; }
      if (!form.value.permissions.length) { errorMessage.value = "Select at least one permission."; return; }

      loading.value = true;
      errorMessage.value = "";
      try {
        await http.patch(`/v2/groups/${props.groupId}/members/${props.member.user_id}`, {
          permissions: [...form.value.permissions],
        });
        toast.success("Member permissions updated successfully");
        emit("updated");
        emit("close");
      } catch (err) {
        console.error("Failed to update member", err);
        errorMessage.value = err?.response?.data?.error?.message || "Unable to update member permissions.";
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => { loadPermissionCatalog(); });

    return { open, loading, catalogLoading, errorMessage, form, hasChanges, getInitials, handleSubmit, permissionCatalog };
  },
};
</script>

<style scoped>
.egmm-form {
  display: grid;
  gap: 1rem;
}

.egmm-member-card {
  align-items: center;
  background: color-mix(in srgb, var(--hs-primary) 6%, transparent);
  border: 1px solid color-mix(in srgb, var(--hs-primary) 20%, transparent);
  border-radius: 12px;
  display: flex;
  gap: 0.85rem;
  padding: 0.9rem 1rem;
}

.egmm-avatar {
  align-items: center;
  background: color-mix(in srgb, var(--hs-primary) 20%, transparent);
  border-radius: 50%;
  color: var(--hs-primary);
  display: flex;
  flex-shrink: 0;
  font-size: 0.85rem;
  font-weight: 800;
  height: 2.4rem;
  justify-content: center;
  width: 2.4rem;
}

.egmm-member-name {
  color: var(--hs-text);
  font-size: 0.9rem;
  font-weight: 700;
  margin: 0;
}

.egmm-member-email {
  color: var(--hs-muted);
  font-size: 0.78rem;
  margin: 0.15rem 0 0;
}

.egmm-footer {
  display: flex;
  gap: 0.6rem;
  justify-content: flex-end;
}
</style>
